import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';  // Use useNavigate from React Router v6
import { Case, Errors } from '../../types';
import { createCase } from '../../api/cases';
import { trackEvent } from '../../utils/analytics';
import { CASE_TYPE_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS } from '../../constants/constants';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';

function CreateCase() {
    // Get today's date in YYYY-MM-DD format for the date input
    const getTodayDate = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // State for the form data (Case object)
    const [formData, setFormData] = useState<Case>({
      case_number: '',
      case_title: '',
      case_type: '',
      client: '',
      assigned_accountants: '',
      start_date: getTodayDate(),
      priority: 'Medium',
      status: 'Open',
    });
  
    // State for validation errors (Errors object)
    const [errors, setErrors] = useState<Errors>({});

    // Guards against double-submit (e.g. double-clicking Create) firing createCase twice
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // React Router navigation hook
    const navigate = useNavigate();  // Hook to navigate
  
    const auth = useAuth();
    const { organization } = useOrganization();

    // default client to current organization if available
    React.useEffect(() => {
      if (organization?.organization_name) {
        setFormData(prev => ({ ...prev, client: organization.organization_name }));
      }
    }, [organization]);
  
    // Handle change for form inputs
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
    };
  
    // Form validation
    const validateForm = (): Errors => {
      const newErrors: Errors = {};
      if (!formData.case_number) newErrors.case_number = 'Case number is required';
      if (!formData.case_title) newErrors.case_title = 'Case title is required';
      if (!formData.case_type) newErrors.case_type = 'Case type is required';
      // client/start_date/priority/status are defaulted and not required in the form UI
      return newErrors;
    };
  
    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (isSubmitting) return;

      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        // Log auth state
        console.log('Auth state:', {
          isAuthenticated: auth.isAuthenticated,
          token: auth.user?.id_token
        });
        
      const response = await createCase(formData, auth.user?.id_token);
      const newCaseId = response?.data?.case_id;
      if (newCaseId) {
        trackEvent('case_created', { case_id: newCaseId, case_type: formData.case_type as string });
        navigate(`/case/${newCaseId}`);
      } else {
        // fallback to cases list if no id returned
        navigate('/cases');
      }
      } catch (error) {
        console.error('Create case error:', error);
        setErrors({ apiError: 'There was an error creating the case. Please try again.' });
        setIsSubmitting(false);
      }
    };
  
    return (
      <div className="container mt-5">
        <h1>Create a New Case</h1>
        <form onSubmit={handleSubmit}>
          {/* Case Number */}
          <div className="mb-3">
            <label htmlFor="case_number" className="form-label">Case Number</label>
            <input
              type="text"
              className={`form-control ${errors.case_number ? 'is-invalid' : ''}`}
              id="case_number"
              name="case_number"
              value={formData.case_number}
              onChange={handleChange}
            />
            {errors.case_number && <div className="invalid-feedback">{errors.case_number}</div>}
          </div>
  
          {/* Case Title */}
          <div className="mb-3">
            <label htmlFor="case_title" className="form-label">Case Title</label>
            <input
              type="text"
              className={`form-control ${errors.case_title ? 'is-invalid' : ''}`}
              id="case_title"
              name="case_title"
              value={formData.case_title}
              onChange={handleChange}
            />
            {errors.case_title && <div className="invalid-feedback">{errors.case_title}</div>}
          </div>
  
          {/* Case Type */}
          <div className="mb-3">
            <label htmlFor="case_type" className="form-label">Case Type</label>
            <select
              className={`form-select ${errors.case_type ? 'is-invalid' : ''}`}
              id="case_type"
              name="case_type"
              value={formData.case_type}
              onChange={handleChange}
            >
              <option value="">Select Case Type</option>
              {CASE_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {errors.case_type && <div className="invalid-feedback">{errors.case_type}</div>}
          </div>
  
          {/* Hidden defaults: client, start_date, priority, status */}
          <input type="hidden" name="client" value={formData.client} />
          <input type="hidden" name="start_date" value={formData.start_date} />
          <input type="hidden" name="priority" value={formData.priority} />
          <input type="hidden" name="status" value={formData.status} />
  
          {/* Submit Button */}
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Case'}
          </button>
          {errors.apiError && <div className="alert alert-danger mt-3">{errors.apiError}</div>}
        </form>
      </div>
    );
  }
  
  export default CreateCase;